// AYTM-12710: derive axes / selections / SKU finders for the multi-axis variant selector.
// See specs/questions/ecommerce/product-family.md §Variant Selector.

import { useCallback, useMemo } from 'react'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../types'
import { useChildrenProducts } from './useChildrenProducts'
import {
  Axis,
  buildAxes,
  findMatchingSKU,
  findSKUForValue,
  isCombinationAvailable,
  isCompatibleWithUpperAxes,
  selectionsOf
} from '../utils/familyAxes'

interface IUseFamilyAxesProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
}

export interface IFamilyAxes {
  childrenProducts: IEcommerceAnswerSchema[]
  axes: Axis[]
  currentSelections: Record<string, string>
  findMatchingSKU: (target: Record<string, string>) => IEcommerceAnswerSchema | undefined
  findSKUForValue: (axisName: string, value: string) => IEcommerceAnswerSchema | undefined
  isCombinationAvailable: (axisName: string, value: string) => boolean
  isCompatibleWithUpperAxes: (axisIndex: number, value: string) => boolean
  isHiddenInPreview: (axisName: string, value: string) => boolean
}

// A preview-hidden (fakeHidden) child keeps its axis values but is never a finder result: its value reads hidden
// when no live sibling carries it, or when the cell it forms with the current selections exists only hidden.
export const useFamilyAxes = ({ answer, schema }: IUseFamilyAxesProps): IFamilyAxes => {
  const childrenProducts = useChildrenProducts({ answer, schema })
  const selectable = useMemo(() => childrenProducts.filter(product => !product.fakeHidden), [childrenProducts])
  const hiddenMembers = useMemo(() => childrenProducts.filter(product => product.fakeHidden), [childrenProducts])

  const axes = useMemo(() => buildAxes(childrenProducts), [childrenProducts])

  const currentSelections = useMemo(() => selectionsOf(answer), [answer])

  const isHiddenInPreviewCb = useCallback(
    (axisName: string, value: string) => {
      const onlyHidden = (target: Record<string, string>) =>
        !findMatchingSKU(selectable, target) && Boolean(findMatchingSKU(hiddenMembers, target))
      return onlyHidden({ [axisName]: value }) || onlyHidden({ ...currentSelections, [axisName]: value })
    },
    [selectable, hiddenMembers, currentSelections]
  )
  const findMatchingSKUCb = useCallback(
    (target: Record<string, string>) => findMatchingSKU(selectable, target),
    [selectable]
  )
  const findSKUForValueCb = useCallback(
    (axisName: string, value: string) =>
      isHiddenInPreviewCb(axisName, value)
        ? undefined
        : findSKUForValue(selectable, currentSelections, axisName, value),
    [selectable, currentSelections, isHiddenInPreviewCb]
  )
  const isCombinationAvailableCb = useCallback(
    (axisName: string, value: string) => isCombinationAvailable(selectable, currentSelections, axisName, value),
    [selectable, currentSelections]
  )
  const isCompatibleWithUpperAxesCb = useCallback(
    (axisIndex: number, value: string) =>
      isCompatibleWithUpperAxes(selectable, axes, currentSelections, axisIndex, value),
    [selectable, axes, currentSelections]
  )
  return {
    childrenProducts,
    axes,
    currentSelections,
    findMatchingSKU: findMatchingSKUCb,
    findSKUForValue: findSKUForValueCb,
    isCombinationAvailable: isCombinationAvailableCb,
    isCompatibleWithUpperAxes: isCompatibleWithUpperAxesCb,
    isHiddenInPreview: isHiddenInPreviewCb
  }
}
